// ─────────────────────────────────────────────────────────────────────────────
// PmfStoryPanel.tsx
//
// Displays the PMF story captured during a scan.
//
// Three sections:
//   1. Summary counts + diagnostics (image capture debug info)
//   2. Fit frames JSON — the slice from first Fit to last Fit frame only,
//      with pmfStatus shown as Swift string labels ("noFit"/"marginalFit"/"fit")
//   3. Frame thumbnails (scrollable list, Fit frames only)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import type {
  ResolvedFrameData,
  PmfStoryDiagnostics,
  StripAnalysis,
  EngineConfig,
  TestConfig,
} from '@novarumdx/ndx-imaging-web';
import { serializeAnalysisResult } from '@novarumdx/ndx-imaging-web';

// ─── PMF status helpers ───────────────────────────────────────────────────────

const PMF_LABEL: Record<number, string> = { 0: 'noFit', 1: 'marginalFit', 2: 'fit' };
const PMF_COLOR: Record<number, 'default' | 'warning' | 'success'> = {
  0: 'default',
  1: 'warning',
  2: 'success',
};

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PmfStoryPanelProps {
  story: ResolvedFrameData[];
  sessionId: string;
  diagnostics: PmfStoryDiagnostics | null;
  strips: StripAnalysis[];
  engineConfig: EngineConfig;
  testConfig?: TestConfig;
}

// ─── Component ────────────────────────────────────────────────────────────────

const PmfStoryPanel: React.FC<PmfStoryPanelProps> = ({ story, sessionId, diagnostics, strips, engineConfig, testConfig }) => {
  const [showJson, setShowJson] = useState(false);
  const [showFrames, setShowFrames] = useState(false);
  const [showStrips, setShowStrips] = useState(false);
  const [showDiag, setShowDiag] = useState(true);

  // Slice from first Fit frame to last Fit frame (inclusive)
  const fitSlice = useMemo(() => {
    const firstFit = story.findIndex((f) => f.pmfStatus === 2);
    const lastFit = [...story].reverse().findIndex((f) => f.pmfStatus === 2);
    if (firstFit === -1) return [];
    const lastFitIdx = story.length - 1 - lastFit;
    return story.slice(firstFit, lastFitIdx + 1);
  }, [story]);
  const actualFitCount = story.filter((f) => f.pmfStatus === 2).length;

  // Fit frames with images
  const fitFramesWithImages = fitSlice.filter((f) => f.imageObjectUrl);
  const fitFramesWithStrips = fitSlice.filter(
    (f) => f.stripImageObjectUrls?.some(Boolean),
  );

  const imageCount = story.filter((f) => f.imageUrl !== null).length;

  // Single representative fit frame for the JSON preview — first fit in the story
  const singleFitFrame = useMemo(
    () => {
      const f = story.find((f) => f.pmfStatus === 2);
      return f ? [f] : fitSlice.slice(0, 1);
    },
    [story, fitSlice],
  );

  // Serialize full envelope for the fit slice
  const envelopeJson = useMemo(
    () => serializeAnalysisResult(engineConfig, strips, singleFitFrame, {}, testConfig),
    [engineConfig, strips, singleFitFrame, testConfig],
  );

  function handleExport() {
    const blob = new Blob([envelopeJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sessionId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (story.length === 0) return null;

  return (
    <Card
      elevation={0}
      sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
    >
      <CardContent>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}
          >
            PMF Story
          </Typography>
          <Tooltip title="Export full envelope JSON (test_configuration + test_strips + pmf_story)">
            <IconButton size="small" onClick={handleExport} aria-label="Export PMF story as JSON">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        {/* ── Summary counts ───────────────────────────────────────────────── */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <Chip size="small" label={`${story.length} total frames`} variant="outlined" />
          <Chip size="small" label={`${actualFitCount} fit frames`} variant="outlined" color="success" />
          <Chip size="small" label={`${fitSlice.length} frames in fit window`} variant="outlined" />
          <Chip
            size="small"
            label={`${imageCount} images`}
            variant="outlined"
            color={imageCount > 0 ? 'primary' : 'default'}
          />
        </Stack>

        {/* ── Diagnostics ──────────────────────────────────────────────────── */}
        {diagnostics && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setShowDiag((v) => !v)}
              sx={{ cursor: 'pointer', userSelect: 'none', py: 0.5 }}
            >
              <Typography variant="body2" fontWeight={500}>
                Image capture diagnostics
              </Typography>
              {showDiag ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Stack>
            <Collapse in={showDiag}>
              <Box
                sx={{
                  mt: 0.5,
                  mb: 1,
                  p: 1.5,
                  bgcolor: diagnostics.imagesSucceeded > 0 ? 'success.50' : 'warning.50',
                  border: '1px solid',
                  borderColor: diagnostics.imagesSucceeded > 0 ? 'success.200' : 'warning.200',
                  borderRadius: 1,
                  fontSize: 12,
                  fontFamily: 'monospace',
                }}
              >
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                  OffscreenCanvas: <strong>{diagnostics.offscreenCanvasSupported ? '✓ supported' : '✗ NOT supported'}</strong>
                </Typography>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                  Lux source:{' '}
                  <strong>
                    {diagnostics.luxSource === 'hardware'
                      ? '✓ hardware (ISO/exposure)'
                      : '~ pixel estimate (iOS/Firefox)'}
                  </strong>
                </Typography>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                  Images attempted: <strong>{diagnostics.imagesAttempted}</strong>
                </Typography>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                  Images succeeded: <strong>{diagnostics.imagesSucceeded}</strong>
                </Typography>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                  Images failed: <strong>{diagnostics.imagesFailed}</strong>
                </Typography>
                {diagnostics.firstError && (
                  <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', color: 'error.main', mt: 0.5 }}>
                    First error: {diagnostics.firstError}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </>
        )}

        {/* ── Fit frame thumbnails ─────────────────────────────────────────── */}
        {fitFramesWithImages.length > 0 && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setShowFrames((v) => !v)}
              sx={{ cursor: 'pointer', userSelect: 'none', py: 0.5 }}
            >
              <Typography variant="body2" fontWeight={500}>
                Fit frame images ({fitFramesWithImages.length})
              </Typography>
              {showFrames ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Stack>
            <Collapse in={showFrames}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  mt: 1,
                  mb: 1,
                }}
              >
                {fitFramesWithImages.map((frame, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={frame.imageObjectUrl!}
                    alt={`Fit frame ${i}`}
                    sx={{
                      width: 80,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 0.5,
                      bgcolor: 'black',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                ))}
              </Box>
            </Collapse>
          </>
        )}

        {/* ── Strip image thumbnails ─────────────────────────────────────── */}
        {fitFramesWithStrips.length > 0 && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setShowStrips((v) => !v)}
              sx={{ cursor: 'pointer', userSelect: 'none', py: 0.5 }}
            >
              <Typography variant="body2" fontWeight={500}>
                Strip images ({fitFramesWithStrips.length} frames)
              </Typography>
              {showStrips ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Stack>
            <Collapse in={showStrips}>
              <Stack spacing={1} sx={{ mt: 1, mb: 1 }}>
                {fitFramesWithStrips.map((frame, i) => (
                  <Box key={i}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
                    >
                      Frame {fitSlice.indexOf(frame)} - {PMF_LABEL[frame.pmfStatus]}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {(frame.stripImageObjectUrls ?? []).map((url, si) =>
                        url ? (
                          <Box
                            key={si}
                            component="img"
                            src={url}
                            alt={`Strip ${si}`}
                            sx={{
                              height: 80,
                              width: 'auto',
                              maxWidth: 120,
                              objectFit: 'contain',
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'black',
                            }}
                          />
                        ) : (
                          <Box
                            key={si}
                            sx={{
                              height: 80,
                              width: 60,
                              bgcolor: 'grey.200',
                              borderRadius: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="caption" color="text.disabled">
                              S{si}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </>
        )}

        {/* ── Fit frames JSON ──────────────────────────────────────────────── */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => setShowJson((v) => !v)}
          sx={{ cursor: 'pointer', userSelect: 'none', py: 0.5 }}
        >
          <Typography variant="body2" fontWeight={500}>
            Full Envelope JSON (1 fit frame preview)
          </Typography>
          {showJson ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Stack>

        <Collapse in={showJson}>
          <Box
            component="pre"
            sx={{
              mt: 1,
              p: 1.5,
              bgcolor: 'grey.100',
              borderRadius: 1,
              fontSize: 10,
              lineHeight: 1.5,
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: 400,
              whiteSpace: 'pre',
              fontFamily: 'monospace',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {envelopeJson}
          </Box>
        </Collapse>

      </CardContent>
    </Card>
  );
};

export default PmfStoryPanel;
